import type { HfdRoutineEffectSnapshot } from "./routineLightingSnapshotIdb";

/** 无快照/字段缺失时兜底：常亮(mode=0) + 100% 亮度/速度 + 多彩（非单色） */
export const ROUTINE_LIGHTING_FALLBACK_MODE = 0;

export const ROUTINE_LIGHTING_FALLBACK_BASE = {
  open: "Open" as const,
  mode: ROUTINE_LIGHTING_FALLBACK_MODE,
  id: ROUTINE_LIGHTING_FALLBACK_MODE,
  luminance: 100,
  speed: 100,
  direction: "Forward" as const,
  /** 0 = 默认多彩盘，非单色静态 */
  selectStaticColor: 0,
};

/** 页面初始占位 / 律动态 UI 残留（常亮 0%/0%），不可当作可恢复快照 */
export function isPlaceholderRoutineLightingSnapshot(
  snap: Record<string, unknown> | null | undefined
): boolean {
  if (!snap || typeof snap !== "object") return true;
  const mode = Number(snap.mode);
  const lum = Number(snap.luminance ?? snap.brightness);
  const speed = Number(snap.speed);
  return mode === 0 && lum === 0 && speed === 0;
}

/**
 * 星闪设备侧 getLightingBase 为 0–20 亮度 / 0–4 速度档位；
 * 灯光页 UI 与 setLightingBase 入参为 0–100 百分比。
 */
export const XINGSHAN_ROUTINE_LUMINANCE_SDK_MAX = 20;
export const XINGSHAN_ROUTINE_SPEED_SDK_MAX = 4;

/** 星闪 SDK 档位 → UI 0–100 */
export function xingshanRoutineLuminanceSdkToUi(value: unknown): number {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) {
    return ROUTINE_LIGHTING_FALLBACK_BASE.luminance;
  }
  if (n > XINGSHAN_ROUTINE_LUMINANCE_SDK_MAX) {
    return Math.round(Math.min(100, n));
  }
  return Math.round((n / XINGSHAN_ROUTINE_LUMINANCE_SDK_MAX) * 100);
}

export function xingshanRoutineSpeedSdkToUi(value: unknown): number {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) {
    return ROUTINE_LIGHTING_FALLBACK_BASE.speed;
  }
  if (n > XINGSHAN_ROUTINE_SPEED_SDK_MAX) {
    return Math.round(Math.min(100, n));
  }
  return Math.round((n / XINGSHAN_ROUTINE_SPEED_SDK_MAX) * 100);
}

/** 快照亮度/速度为 UI 0–100（灯光页写入）；无此标记且值在 SDK 档位内则恢复时换算 */
export const XINGSHAN_UI_PERCENT_SCALE_FLAG = "__xingshanUiPercentScale";

/**
 * 判断是否为 getLightingBase 的 SDK 档位读数（如满档 20/4），而非灯光页 UI 百分比。
 * 二者在数值上可能重叠（UI 20% 也是 20），但 SDK 读数不会超过 20/4 上界。
 */
export function isLikelyXingshanSdkScaleSnapshot(
  snap: Record<string, unknown>
): boolean {
  const lum = Number(snap.luminance ?? snap.brightness);
  const speed = Number(snap.speed);
  const lumLooksSdk =
    Number.isFinite(lum) &&
    lum >= 0 &&
    lum <= XINGSHAN_ROUTINE_LUMINANCE_SDK_MAX;
  const speedLooksSdk =
    Number.isFinite(speed) &&
    speed >= 0 &&
    speed <= XINGSHAN_ROUTINE_SPEED_SDK_MAX;
  /** 典型 getLightingBase 满档 20/4；两侧都在 SDK 档位内才视为 SDK 读数，避免 UI 20% 被误换算 */
  if (lumLooksSdk && speedLooksSdk) return true;
  /** 仅一侧有值时（旧快照缺字段）仍按 SDK 档位处理 */
  if (lumLooksSdk && !Number.isFinite(speed)) return true;
  if (speedLooksSdk && !Number.isFinite(lum)) return true;
  return false;
}

function coerceXingshanFieldToUiStorage(
  snap: Record<string, unknown>
): Record<string, unknown> {
  const lumRaw = snap.luminance ?? snap.brightness;
  const speedRaw = snap.speed;
  const lumNum = Number(lumRaw);
  const speedNum = Number(speedRaw);
  const out = { ...snap };
  if (
    Number.isFinite(lumNum) &&
    lumNum >= 0 &&
    lumNum <= XINGSHAN_ROUTINE_LUMINANCE_SDK_MAX
  ) {
    out.luminance = xingshanRoutineLuminanceSdkToUi(lumRaw);
  }
  if (
    Number.isFinite(speedNum) &&
    speedNum >= 0 &&
    speedNum <= XINGSHAN_ROUTINE_SPEED_SDK_MAX
  ) {
    out.speed = xingshanRoutineSpeedSdkToUi(speedRaw);
  }
  return out;
}

function hasAnyXingshanFieldClearlyUi(snap: Record<string, unknown>): boolean {
  const lum = Number(snap.luminance ?? snap.brightness);
  const speed = Number(snap.speed);
  return (
    (Number.isFinite(lum) && lum > XINGSHAN_ROUTINE_LUMINANCE_SDK_MAX) ||
    (Number.isFinite(speed) && speed > XINGSHAN_ROUTINE_SPEED_SDK_MAX)
  );
}

/** 写入 IDB / 恢复前：把 SDK 档位读数统一成 UI 0–100；已是 UI 百分比则原样保留 */
export function coerceXingshanRoutineBaseToUiStorage(
  snap: Record<string, unknown>,
  options?: { trustAsUiPercent?: boolean }
): Record<string, unknown> {
  /** 误标 UI 但仍是 SDK 满档 20/4 时，忽略标记并换算 */
  if (
    snap[XINGSHAN_UI_PERCENT_SCALE_FLAG] === true &&
    isLikelyXingshanSdkScaleSnapshot(snap) &&
    !hasAnyXingshanFieldClearlyUi(snap)
  ) {
    return {
      ...coerceXingshanFieldToUiStorage(snap),
      [XINGSHAN_UI_PERCENT_SCALE_FLAG]: true,
    };
  }

  const trustUi =
    options?.trustAsUiPercent === true &&
    (!isLikelyXingshanSdkScaleSnapshot(snap) ||
      hasAnyXingshanFieldClearlyUi(snap));
  if (trustUi || snap[XINGSHAN_UI_PERCENT_SCALE_FLAG] === true) {
    return { ...snap, [XINGSHAN_UI_PERCENT_SCALE_FLAG]: true };
  }
  if (isLikelyXingshanSdkScaleSnapshot(snap)) {
    return {
      ...coerceXingshanFieldToUiStorage(snap),
      [XINGSHAN_UI_PERCENT_SCALE_FLAG]: true,
    };
  }
  const lumNum = Number(snap.luminance ?? snap.brightness);
  const speedNum = Number(snap.speed);
  const alreadyUi =
    (Number.isFinite(lumNum) && lumNum > XINGSHAN_ROUTINE_LUMINANCE_SDK_MAX) ||
    (Number.isFinite(speedNum) && speedNum > XINGSHAN_ROUTINE_SPEED_SDK_MAX);
  if (alreadyUi) {
    return { ...snap, [XINGSHAN_UI_PERCENT_SCALE_FLAG]: true };
  }
  return {
    ...snap,
    luminance: xingshanRoutineLuminanceSdkToUi(
      snap.luminance ?? snap.brightness
    ),
    speed: xingshanRoutineSpeedSdkToUi(snap.speed),
    [XINGSHAN_UI_PERCENT_SCALE_FLAG]: true,
  };
}

/** @deprecated 使用 coerceXingshanRoutineBaseToUiStorage */
export function normalizeXingshanRoutineBaseToUiScale(
  snap: Record<string, unknown>
): Record<string, unknown> {
  return coerceXingshanRoutineBaseToUiStorage(snap);
}

/** 星闪常规灯效 mode：优先 mode 字段（id 可能来自其它语义，且 0=常亮 合法） */
export function resolveXingshanRoutineModeFromSnapshot(
  snap: Record<string, unknown>,
  fallbackMode = ROUTINE_LIGHTING_FALLBACK_MODE
): number {
  const mode = Number(snap.mode);
  if (Number.isFinite(mode) && mode !== 20) {
    return mode;
  }
  const idField = Number(snap.id);
  if (Number.isFinite(idField) && idField !== 20 && idField <= 19) {
    return idField;
  }
  const fb = Number(fallbackMode);
  if (Number.isFinite(fb) && fb !== 20) {
    return fb;
  }
  return ROUTINE_LIGHTING_FALLBACK_MODE;
}

/** 星闪恢复 / switchParams 统一下发 payload：先 UI 换算，再补全默认值 */
export function buildXingshanRoutineBaseRestorePayload(
  snap: Record<string, unknown>,
  resolvedMode: number
): Record<string, unknown> {
  const uiSnap = coerceXingshanRoutineBaseToUiStorage(snap);
  const mode = resolveXingshanRoutineModeFromSnapshot(uiSnap, resolvedMode);
  return buildRoutineBaseRestorePayload(uiSnap, mode);
}

/** 与灯光页 switchParams(nextParams) 下发 payload 一致 */
export function buildXingshanSwitchParamsStyleData(
  snap: Record<string, unknown>,
  resolvedMode: number
): Record<string, unknown> {
  return buildXingshanRoutineBaseRestorePayload(snap, resolvedMode);
}

export function isSetLightingBaseResponseOk(res: unknown): boolean {
  if (res == null) return true;
  if (typeof res !== "object") return true;
  const code = (res as { code?: unknown }).code;
  return code === undefined || code === 200;
}

export type XingshanQuitLightingApi = {
  setLightingBase?: (p: unknown) => Promise<unknown>;
  setLightingCustom?: (p: unknown) => Promise<unknown>;
};

export function createEmptyXingshanCustomLightingGrid(): unknown[] {
  return Array.from({ length: 6 }, () =>
    Array.from({ length: 21 }, () => ({ isCustom: false }))
  );
}

/**
 * 星闪退出律动写硬件：须先 setLightingBase 脱离 mode=20，再清 custom 残帧。
 * 若先清 custom 而 base 未写上，实体会全黑。
 */
export async function applyXingshanRoutineQuitHardwareRestore(
  api: XingshanQuitLightingApi,
  baseSnap: Record<string, unknown>,
  fallbackMode = ROUTINE_LIGHTING_FALLBACK_MODE
): Promise<boolean> {
  if (!api?.setLightingBase) return false;

  const resolvedMode = resolveXingshanRoutineModeFromSnapshot(
    baseSnap,
    fallbackMode
  );
  const payload = buildXingshanRoutineBaseRestorePayload(
    baseSnap,
    resolvedMode
  );

  let baseRes: unknown;
  try {
    baseRes = await api.setLightingBase({
      area: "Keyboard",
      config: "Base",
      lamp: "SingleLighting",
      data: payload,
    });
  } catch (e) {
    console.warn("[XingshanQuitRestore] setLightingBase 失败:", e);
    return false;
  }

  if (!isSetLightingBaseResponseOk(baseRes)) {
    console.warn(
      "[XingshanQuitRestore] setLightingBase 未成功，跳过清 custom:",
      baseRes,
      payload
    );
    return false;
  }

  if (api.setLightingCustom) {
    try {
      await api.setLightingCustom({
        area: "Keyboard",
        protocol: "Custom",
        data: createEmptyXingshanCustomLightingGrid(),
      });
    } catch (e) {
      console.warn("[XingshanQuitRestore] 清除律动 custom 残帧失败:", e);
    }
  }
  return true;
}

/** 与灯光页点「是」硬件恢复一致：单次 setLightingBase + 清 custom */
export async function applyXingshanRoutineRestoreLikeLightingPageYes(
  api: XingshanQuitLightingApi,
  baseSnap: Record<string, unknown>,
  fallbackMode = ROUTINE_LIGHTING_FALLBACK_MODE
): Promise<boolean> {
  const uiSnap = coerceXingshanRoutineBaseToUiStorage(baseSnap);
  return applyXingshanRoutineQuitHardwareRestore(api, uiSnap, fallbackMode);
}

/**
 * 桌面端退出 / 子应用关闭：星闪写回常规灯效（与灯光页「是」+ switchParams 二次下发一致）。
 */
export async function restoreXingshanRoutineLightingFromSnapshotForQuit(
  api: XingshanQuitLightingApi,
  baseSnap: Record<string, unknown>,
  fallbackMode = ROUTINE_LIGHTING_FALLBACK_MODE
): Promise<boolean> {
  if (!api?.setLightingBase) return false;

  const uiSnap = coerceXingshanRoutineBaseToUiStorage(baseSnap);
  const resolvedMode = resolveXingshanRoutineModeFromSnapshot(
    uiSnap,
    fallbackMode
  );

  const ok = await applyXingshanRoutineRestoreLikeLightingPageYes(
    api,
    uiSnap,
    resolvedMode
  );
  if (!ok) {
    console.warn("[XingshanQuitRestore] 首次 setLightingBase 未成功");
    return false;
  }

  try {
    const payload = buildXingshanRoutineBaseRestorePayload(
      uiSnap,
      resolvedMode
    );
    const res = await api.setLightingBase({
      area: "Keyboard",
      config: "Base",
      lamp: "SingleLighting",
      data: payload,
    });
    if (!isSetLightingBaseResponseOk(res)) {
      console.warn("[XingshanQuitRestore] 二次 setLightingBase 未成功:", res);
      return false;
    }
    console.log("[XingshanQuitRestore] 已恢复 mode=", resolvedMode, payload);
    return true;
  } catch (e) {
    console.warn("[XingshanQuitRestore] 二次 setLightingBase 失败:", e);
    return false;
  }
}

/** 律动退出恢复后：禁止 SDK 自动回读覆盖刚写回灯效的时长（毫秒） */
export const MUSIC_RHYTHM_QUIT_RESTORE_GUARD_MS = 5000;

let quitRestoreGuardTimer: ReturnType<typeof setTimeout> | null = null;

export function isMusicRhythmQuitRestoreInProgress(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return Boolean((window as any).__musicRhythmQuitRestoreInProgress);
  } catch {
    return false;
  }
}

export function beginMusicRhythmQuitRestoreGuard(): void {
  if (typeof window === "undefined") return;
  if (quitRestoreGuardTimer) {
    clearTimeout(quitRestoreGuardTimer);
    quitRestoreGuardTimer = null;
  }
  try {
    (window as any).__musicRhythmQuitRestoreInProgress = true;
  } catch {
    // ignore
  }
}

export function clearMusicRhythmQuitRestoreGuard(): void {
  if (typeof window === "undefined") return;
  if (quitRestoreGuardTimer) {
    clearTimeout(quitRestoreGuardTimer);
    quitRestoreGuardTimer = null;
  }
  try {
    (window as any).__musicRhythmQuitRestoreInProgress = false;
    (window as any).__keyboardDisableMusicRhythmOutput = false;
  } catch {
    // ignore
  }
}

/** 恢复写硬件完成后，延迟结束 guard，避免 lightingBase / autoLoad 回读覆盖 UI */
export function scheduleEndMusicRhythmQuitRestoreGuard(
  delayMs = MUSIC_RHYTHM_QUIT_RESTORE_GUARD_MS
): void {
  if (typeof window === "undefined") return;
  if (quitRestoreGuardTimer) {
    clearTimeout(quitRestoreGuardTimer);
    quitRestoreGuardTimer = null;
  }
  beginMusicRhythmQuitRestoreGuard();
  quitRestoreGuardTimer = setTimeout(
    () => {
      quitRestoreGuardTimer = null;
      clearMusicRhythmQuitRestoreGuard();
    },
    Math.max(0, delayMs)
  );
}

/** 退出前阻断律动下发，避免清 custom / 写 base 期间又被黑帧覆盖 */
export function haltMusicRhythmHardwareOutputForQuit(): void {
  if (typeof window === "undefined") return;
  beginMusicRhythmQuitRestoreGuard();
  try {
    (window as any).__keyboardDisableMusicRhythmOutput = true;
  } catch {
    // ignore
  }
  try {
    (window as any).electronAudioAPI?.sendAudioControl?.({
      type: "STOP_CAPTURE",
    });
  } catch {
    // ignore
  }
  try {
    window.sessionStorage.setItem(
      "music-management:musicRhythm:captureActive",
      "0"
    );
  } catch {
    // ignore
  }
}

export function buildRoutineBaseRestorePayloadForSolution(
  solution: string,
  snap: Record<string, unknown>,
  resolvedMode: number
): Record<string, unknown> {
  if (String(solution).toLowerCase() === "xingshan") {
    return buildXingshanRoutineBaseRestorePayload(snap, resolvedMode);
  }
  return buildRoutineBaseRestorePayload(snap, resolvedMode);
}

/**
 * 华奋达不可作为「进入律动前常规灯效」快照的模式：
 * - UI 自定义键色 = 20
 * - 固件/SDK 音乐律动 & 自定义键色通道 = 0x80 (128)
 */
export function isHfdNonRestorableLightingMode(mode: unknown): boolean {
  const n = Number(mode);
  if (!Number.isFinite(n)) return true;
  return n === 20 || n === 0x80 || n === 128;
}

/** 华奋达 SDK 档位：5 档即 UI 100% */
export const HFD_ROUTINE_FALLBACK_EFFECT: HfdRoutineEffectSnapshot = {
  mode: ROUTINE_LIGHTING_FALLBACK_MODE,
  red: 255,
  green: 255,
  blue: 255,
  colorMode: 1,
  brightness: 5,
  speed: 5,
  direction: 0,
  effectModeType: 0,
};

function pickPositiveNumber(value: unknown, fallback: number): number {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

export function stripRoutineSnapshotMetadata(
  snap: Record<string, unknown>
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(snap)) {
    if (k.startsWith("__")) continue;
    out[k] = v;
  }
  return out;
}

export function resolveRoutineModeFromSnapshot(
  snap: Record<string, unknown>,
  fallbackMode = ROUTINE_LIGHTING_FALLBACK_MODE
): number {
  const idField = Number(snap.id);
  if (Number.isFinite(idField) && idField !== 20) {
    return idField;
  }
  const mode = Number(snap.mode);
  if (!Number.isNaN(mode) && mode !== 20) {
    return mode;
  }
  const fb = Number(fallbackMode);
  if (Number.isFinite(fb) && fb !== 20) {
    return fb;
  }
  return ROUTINE_LIGHTING_FALLBACK_MODE;
}

/** web-hid 贝盈 getEffect 以 id 为灯效档位；mode 可能是主灯区(0/1)而非灯效 id */
export function normalizeBeiyingRoutineBaseForSnapshot(
  data: Record<string, unknown>
): Record<string, unknown> {
  const id = Number(data.id);
  const mode = Number(data.mode);
  if (Number.isFinite(id) && id !== 20) {
    const effectMode =
      Number.isFinite(mode) && mode !== 20 && mode > 1 ? mode : id;
    return { ...data, id, mode: effectMode };
  }
  if (Number.isFinite(mode) && mode !== 20 && mode > 1) {
    return { ...data, id: mode, mode };
  }
  return data;
}

export function resolveBeiyingRoutineModeFromSnapshot(
  snap: Record<string, unknown>,
  fallbackMode = ROUTINE_LIGHTING_FALLBACK_MODE
): number {
  const idField = Number(snap.id);
  if (Number.isFinite(idField) && idField !== 20) {
    return idField;
  }
  const mode = Number(snap.mode);
  if (!Number.isNaN(mode) && mode !== 20 && mode > 1) {
    return mode;
  }
  return resolveRoutineModeFromSnapshot(snap, fallbackMode);
}

/** 星闪/贝盈 setLightingBase 恢复：补全亮度/速度/多彩默认值 */
export function buildRoutineBaseRestorePayload(
  snap: Record<string, unknown>,
  resolvedMode: number
): Record<string, unknown> {
  const clean = stripRoutineSnapshotMetadata(snap);
  return {
    ...clean,
    open: clean.open === "Close" ? "Close" : "Open",
    mode: resolvedMode,
    id: resolvedMode,
    luminance: pickPositiveNumber(
      clean.luminance ?? clean.brightness,
      ROUTINE_LIGHTING_FALLBACK_BASE.luminance
    ),
    speed: pickPositiveNumber(
      clean.speed,
      ROUTINE_LIGHTING_FALLBACK_BASE.speed
    ),
    direction: clean.direction ?? ROUTINE_LIGHTING_FALLBACK_BASE.direction,
    selectStaticColor:
      clean.selectStaticColor ??
      ROUTINE_LIGHTING_FALLBACK_BASE.selectStaticColor,
  };
}

export function normalizeHfdEffectForRestore(
  hfd: HfdRoutineEffectSnapshot
): HfdRoutineEffectSnapshot {
  const resolvedMode = Number(hfd.mode);
  return {
    ...HFD_ROUTINE_FALLBACK_EFFECT,
    ...hfd,
    mode:
      Number.isFinite(resolvedMode) &&
      !isHfdNonRestorableLightingMode(resolvedMode)
        ? resolvedMode
        : HFD_ROUTINE_FALLBACK_EFFECT.mode,
    brightness: pickPositiveNumber(
      hfd.brightness,
      HFD_ROUTINE_FALLBACK_EFFECT.brightness
    ),
    speed: pickPositiveNumber(hfd.speed, HFD_ROUTINE_FALLBACK_EFFECT.speed),
    colorMode:
      hfd.colorMode === 0 || hfd.colorMode === 1
        ? hfd.colorMode
        : HFD_ROUTINE_FALLBACK_EFFECT.colorMode,
  };
}

export function hasCompleteXingshanRoutineBaseFields(
  snap: Record<string, unknown> | null | undefined
): boolean {
  if (!snap || typeof snap !== "object") return false;
  return snap.luminance !== undefined && snap.speed !== undefined;
}
